import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { Button, Input, Alert } from '@components/ui';
import { useAuth } from '../hooks/useAuth';
import { TEST_IDS } from '@lib/utils/testIds';

/**
 * Map backend error codes to user-friendly messages and alert variants.
 */
function getLoginErrorInfo(error: any): { message: string; variant: 'danger' | 'warning' } {
  const code = error?.response?.data?.code;
  const status = error?.response?.status;

  // Service unavailable (Aludra down, timeout, network issues)
  if (status === 503 || code === 'SERVICE_UNAVAILABLE' || code === 'SERVICE_TIMEOUT' || code === 'SERVICE_ERROR') {
    return {
      message: 'El servicio no está disponible en este momento. Por favor, intenta nuevamente en unos minutos.',
      variant: 'warning',
    };
  }

  // Server error (unexpected failure after authentication succeeded)
  // Must come before the default 401 check so 500s aren't shown as "invalid credentials"
  if (status === 500) {
    return {
      message: 'Ocurrió un error inesperado en el servidor. Por favor, intenta nuevamente en unos minutos.',
      variant: 'warning',
    };
  }

  // Account locked
  if (status === 403 || code === 'ACCOUNT_LOCKED') {
    return {
      message: 'Tu cuenta ha sido bloqueada. Contacta al administrador.',
      variant: 'danger',
    };
  }

  // Auth rate limited
  if (status === 429 || code === 'AUTH_RATE_LIMITED') {
    return {
      message: 'Demasiados intentos de inicio de sesión. Por favor, intenta nuevamente en 15 minutos.',
      variant: 'warning',
    };
  }

  // Two-factor required
  if (code === 'TWO_FACTOR_REQUIRED') {
    return {
      message: 'Se requiere autenticación de dos factores.',
      variant: 'warning',
    };
  }

  // Network error (no response from our own backend)
  if (!error?.response) {
    return {
      message: 'No se pudo conectar con el servidor. Verifica tu conexión a internet e intenta nuevamente.',
      variant: 'warning',
    };
  }

  // Default: invalid credentials (only for actual 401)
  return {
    message: 'Credenciales inválidas. Por favor, verifica tu correo y contraseña.',
    variant: 'danger',
  };
}

const loginSchema = z.object({
  email: z.string().email('Correo electrónico inválido'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
  tenant: z.number().int().positive(),
  rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

// Development credentials from environment variables (NOT hardcoded)
// Set VITE_DEV_EMAIL and VITE_DEV_PASSWORD in your local .env.development
const getDevCredentials = () => {
  const email = import.meta.env.VITE_DEV_EMAIL || '';
  const password = import.meta.env.VITE_DEV_PASSWORD || '';
  const tenant = parseInt(import.meta.env.VITE_DEFAULT_TENANT || '0', 10);

  return { email, password, tenant };
};

// Check if dev credentials are configured
const hasDevCredentials = () => {
  return !!(import.meta.env.VITE_DEV_EMAIL && import.meta.env.VITE_DEV_PASSWORD);
};

export function LoginForm() {
  const { login, isLoggingIn, loginError, isAuthenticated } = useAuth();
  const defaultTenant = parseInt(import.meta.env.VITE_DEFAULT_TENANT || '0', 10);
  const isDevelopment = import.meta.env.VITE_APP_ENV === 'development';
  const autoLoginEnabled = import.meta.env.VITE_AUTO_LOGIN === 'true';
  const devCredentialsConfigured = hasDevCredentials();
  const devCredentials = getDevCredentials();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: isDevelopment && autoLoginEnabled && devCredentialsConfigured ? devCredentials.email : '',
      password: isDevelopment && autoLoginEnabled && devCredentialsConfigured ? devCredentials.password : '',
      tenant: defaultTenant,
      rememberMe: false,
    },
  });

  // Auto-login in development mode (only if enabled AND credentials are configured in env)
  useEffect(() => {
    if (isDevelopment && autoLoginEnabled && devCredentialsConfigured && !isAuthenticated && !isLoggingIn && !loginError) {
      console.log('🔐 Auto-login in development mode...');
      login({
        email: devCredentials.email,
        password: devCredentials.password,
        tenant: devCredentials.tenant,
        rememberMe: true,
      });
    }
  }, [isDevelopment, autoLoginEnabled, devCredentialsConfigured, isAuthenticated, isLoggingIn, loginError, login, devCredentials]);

  const onSubmit = (data: LoginFormData) => {
    login(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid={TEST_IDS.AUTH.LOGIN.FORM}>
      {isDevelopment && autoLoginEnabled && devCredentialsConfigured && (
        <Alert variant="info" title="Modo Desarrollo">
          Auto-login activado con credenciales de entorno
        </Alert>
      )}

      {loginError && (() => {
        const errorInfo = getLoginErrorInfo(loginError);
        return (
          <Alert variant={errorInfo.variant} title="Error al iniciar sesión" data-testid={TEST_IDS.AUTH.LOGIN.ERROR_ALERT}>
            {errorInfo.message}
          </Alert>
        );
      })()}

      <Input
        label="Correo Electrónico"
        type="email"
        placeholder="correo@ejemplo.com"
        error={errors.email?.message}
        data-testid={TEST_IDS.AUTH.LOGIN.EMAIL_INPUT}
        {...register('email')}
      />

      <Input
        label="Contraseña"
        type="password"
        placeholder="••••••••"
        error={errors.password?.message}
        data-testid={TEST_IDS.AUTH.LOGIN.PASSWORD_INPUT}
        {...register('password')}
      />

      {/* Tenant field - hidden from user, set automatically */}
      <input type="hidden" {...register('tenant', { valueAsNumber: true })} />

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <input
            id="rememberMe"
            type="checkbox"
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
            data-testid={TEST_IDS.AUTH.LOGIN.REMEMBER_CHECKBOX}
            {...register('rememberMe')}
          />
          <label htmlFor="rememberMe" className="ml-2 block text-sm text-secondary-700">
            Recordarme
          </label>
        </div>
        <div className="text-sm">
          <Link
            to="/forgot-password"
            className="font-medium text-primary-600 hover:text-primary-500"
            data-testid={TEST_IDS.AUTH.LOGIN.FORGOT_PASSWORD_LINK}
          >
            ¿Olvidaste tu contraseña?
          </Link>
        </div>
      </div>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="w-full"
        isLoading={isLoggingIn}
        data-testid={TEST_IDS.AUTH.LOGIN.SUBMIT_BUTTON}
      >
        Iniciar Sesión
      </Button>

      <div className="mt-4 text-center text-sm text-secondary-600 dark:text-secondary-400">
        <p>Impulsado por hypernovalabs</p>
      </div>
    </form>
  );
}
